import * as ort from 'onnxruntime-web';
import { PreloadConfig, ResolutionConfig, resourceLoader } from '@webex/ladon-ts';

import { loadAndCacheFile } from './util';

// There is only a single WASM file for all WebAssembly binaries now.
const ONNX_WASM_FILENAME = 'ort-wasm-simd-threaded.wasm';

/**
 * Resource manager used to load resources for the segmentation worker.
 */
export class ModelManager {
    private modelSessions: Record<string, ort.InferenceSession> = {};

    /**
     * Load the model using the provided configuration. This gets called by the worker when using
     * the worker generator.
     *
     * @param config - The configuration object.
     * @returns A promise that resolves when the model is preloaded.
     */
    public async loadModel(config: PreloadConfig) {
        const { mask, executionProviders: providers } = config;

        // Lock to a single thread to avoid performance issues.
        ort.env.wasm.numThreads = 1;

        // Get the resolved paths for the WASM binary.
        const wasmPaths = await resourceLoader.resolveUri(config, config.wasmUri);

        if (wasmPaths) {
            try {
                ort.env.wasm.wasmBinary = await loadAndCacheFile(wasmPaths + ONNX_WASM_FILENAME);
            } catch (error) {
                console.warn('Failed to load or cache WASM file. Using WASM paths.', error);
                ort.env.wasm.wasmPaths = wasmPaths;
            }
        }

        // Resolve the model URL.
        const modelUrl = await resourceLoader.resolveUri(config, mask.modelUri);
        let modelSession = this.modelSessions[modelUrl];

        // If the model session is not already cached, create a new session and cache.
        if (!modelSession) {
            // Allows the user to specify the execution providers to use. The default is the WASM provider.
            // TODO: Add and test support for other execution providers.
            const executionProviders = providers?.length ? providers : ['wasm'];
            const sessionOptions = { executionProviders };

            // First try to get the model binary either from cache or by fetching and caching it.
            // If that fails, fall back to the model URL.
            try {
                const modelBinary = await loadAndCacheFile(modelUrl);
                modelSession = await ort.InferenceSession.create(modelBinary, sessionOptions);
            } catch (error) {
                console.warn('Failed to load or cache model file. Using model URL.', error);
                modelSession = await ort.InferenceSession.create(modelUrl, sessionOptions);
            }
        }

        // Cache the model session.
        this.modelSessions[modelUrl] = modelSession;

        // Warmup the model with a dummy input to avoid latency when running the model for the first time.
        await this.warmupModel(modelSession, mask.inputSize, mask.modelRank);

        return modelSession;
    }

    /**
     * Warmup the model session with a dummy input to avoid latency when running the model for the first time.
     *
     * @param {ort.InferenceSession} session The model session.
     * @param {ResolutionConfig} inputSize The input size of the model.
     * @param {number} inputChannels The number of input channels (e.g., RGB is 3).
     * @param {ort.Tensor.Type} inputType The input tensor type.
     * @returns {Promise<void>} A promise that resolves when the model is warmed up.
     * @example
     * await warmupModel(session, inputSize, inputChannels, inputType);
     */
    public async warmupModel(
        session: ort.InferenceSession,
        inputSize: ResolutionConfig = { width: 1280, height: 720 },
        inputChannels = 3,
        inputType: ort.Tensor.Type = 'float32'
    ): Promise<void> {
        const numPixels = inputSize.height * inputSize.width;
        const dummyInputData = new Float32Array(inputChannels * numPixels).fill(0);
        const dummyInput = new ort.Tensor(inputType, dummyInputData, [
            1,
            inputChannels,
            inputSize.height,
            inputSize.width,
        ]);
        const feed = { [session.inputNames[0]]: dummyInput };

        // Use an additional dummy tensor if the model expects a previous mask.
        if (inputChannels === 3) {
            const dummyMaskData = new Float32Array(numPixels).fill(0);
            const prevMaskTensor = new ort.Tensor(inputType, dummyMaskData, [
                1,
                1,
                inputSize.height,
                inputSize.width,
            ]);
            feed[session.inputNames[1]] = prevMaskTensor;
        }

        // Run the model with the dummy input to warm it up.
        await session.run(feed);
    }

    /**
     * Get the model session for the provided model URL.
     *
     * @param {string} modelUrl The model URL.
     * @returns {ort.InferenceSession} The model session.
     * @example
     * const session = getModelSession(modelUrl);
     */
    public getModelSession(modelUrl: string) {
        return this.modelSessions[modelUrl];
    }

    /**
     * Clear the model sessions cache.
     *
     * @example
     * modelManager.destroyModel();
     */
    public async destroyModel(modelUrl: string) {
        const session = this.modelSessions[modelUrl];
        if (session) {
            await session.release();
            delete this.modelSessions[modelUrl];
        }
    }
    
}

export const modelManager = new ModelManager();